<script setup lang="ts">
import { Link, useForm } from "@inertiajs/vue3"
import { UiButton, UiCurrencyInput, UiFormLayout, UiSelect, UiTextInput } from "@/components"
import { useNotifications } from "@/composables/useNotifications"
import { computed } from "vue"

type StockMovementPayload = {
  id?: number | null
  company_id: number | ""
  product_id: number | ""
  movement_type: string
  reason: string
  notes: string
  quantity: string
  unit_cost_cents: string
  reference_type: string
  reference_id: string
}

const props = defineProps<{
  mode: "create" | "edit"
  title: string
  subtitle?: string
  stockMovement: StockMovementPayload
  companies: Array<{ id: number; name: string }>
  products: Array<{ label: string; value: number }>
  movementTypes: Array<{ label: string; value: string }>
  reasonOptions: Array<{ label: string; value: string }>
  errors?: Record<string, string[]>
}>()

const form = useForm({
  stock_movement: {
    company_id: props.stockMovement.company_id,
    product_id: props.stockMovement.product_id,
    movement_type: props.stockMovement.movement_type,
    reason: props.stockMovement.reason,
    notes: props.stockMovement.notes,
    quantity: props.stockMovement.quantity,
    unit_cost_cents: props.stockMovement.unit_cost_cents,
    reference_type: props.stockMovement.reference_type,
    reference_id: props.stockMovement.reference_id,
  },
})

const notify = useNotifications()
const fieldError = (field: string) => props.errors?.[field]?.[0]
const companyOptions = computed(() => props.companies.map((company) => ({ label: company.name, value: company.id })))

function submit() {
  const opts = { onSuccess: () => notify.success(props.mode === "create" ? "Movimentacao criada com sucesso" : "Movimentacao atualizada com sucesso") }
  if (props.mode === "create") {
    form.post("/stock_movements", opts)
    return
  }

  form.put(`/stock_movements/${props.stockMovement.id}`, opts)
}
</script>

<template>
  <UiFormLayout :title="title" :subtitle="subtitle" back-href="/stock_movements" back-label="Voltar para estoque">
    <form class="space-y-4" @submit.prevent="submit">
      <div class="grid gap-4 md:grid-cols-2">
        <UiSelect
          id="stock-movement-company"
          :model-value="form.stock_movement.company_id"
          label="Empresa"
          :options="companyOptions"
          :disabled="mode === 'edit'"
          :error="fieldError('company') || fieldError('company_id')"
          @update:model-value="form.stock_movement.company_id = $event as number | ''"
        />

        <UiSelect
          id="stock-movement-product"
          :model-value="form.stock_movement.product_id"
          label="Produto"
          :options="products"
          :error="fieldError('product') || fieldError('product_id')"
          @update:model-value="form.stock_movement.product_id = $event as number | ''"
        />

        <UiSelect
          id="stock-movement-type"
          v-model="form.stock_movement.movement_type"
          label="Tipo de movimentacao"
          :options="movementTypes"
          :error="fieldError('movement_type')"
        />

        <UiSelect
          id="stock-movement-reason"
          v-model="form.stock_movement.reason"
          label="Motivo"
          :options="reasonOptions"
          :error="fieldError('reason')"
        />

        <UiTextInput
          id="stock-movement-quantity"
          v-model="form.stock_movement.quantity"
          type="number"
          label="Quantidade"
          placeholder="Ex: 10"
          :error="fieldError('quantity')"
          helper-text="Use valor positivo para entrada/saida. Ajuste aceita valor com sinal."
        />

        <UiCurrencyInput
          id="stock-movement-unit-cost"
          v-model="form.stock_movement.unit_cost_cents"
          label="Custo da movimentacao"
          helper-text="Custo registrado especificamente nesta movimentacao"
          :error="fieldError('unit_cost')"
        />

        <UiTextInput
          id="stock-movement-reference-type"
          v-model="form.stock_movement.reference_type"
          label="Tipo de referencia"
          placeholder="Ex: PurchaseOrder"
          :error="fieldError('reference_type')"
        />

        <UiTextInput
          id="stock-movement-reference-id"
          v-model="form.stock_movement.reference_id"
          label="ID de referencia"
          placeholder="Ex: 123"
          :error="fieldError('reference_id')"
        />

        <div class="md:col-span-2">
          <label class="mb-1 block text-sm font-medium text-slate-700" for="stock-movement-notes">Observacoes</label>
          <textarea
            id="stock-movement-notes"
            v-model="form.stock_movement.notes"
            rows="3"
            class="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 transition focus:border-slate-400 focus:outline-none"
            placeholder="Detalhe o contexto do ajuste"
          />
          <p v-if="fieldError('notes')" class="mt-1 text-sm text-rose-600">{{ fieldError('notes') }}</p>
        </div>
      </div>

      <div class="flex items-center gap-3 pt-2">
        <UiButton type="submit" :disabled="form.processing">
          {{ mode === "create" ? "Criar movimentacao" : "Salvar alteracoes" }}
        </UiButton>

        <Link
          href="/stock_movements"
          class="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
        >
          Cancelar
        </Link>
      </div>
    </form>
  </UiFormLayout>
</template>
